<template>
  <v-card class="h-100">
    <v-chart ref="chart" :options="options" :init="chartInit" :ready="chartReady" @contextmenu="contextmenu" />
  </v-card>
</template>

<script>
import { format } from 'date-fns';
import { comma } from '@/utils/formatter';
import chartable from '@/mixins/chartable';

export default {
  mixins: [chartable],
  props: {
    props: Object,
    system: {
      type: Object,
      required: true,
    },
    items: {
      type: Array,
      required: true,
    },
    values: {
      type: Array,
      required: true,
    },
    yAxisMax: {
      type: Number,
      required: true,
    },
    xAxisMax: {
      type: Number,
      default: 50,
    },
    grid: {
      type: Object,
      default() {
        return {
          top: '5px',
          left: '30px',
          right: 0,
          bottom: '20px',
        };
      },
    },
    selectedHandler: {
      type: Function,
      default: p => p,
    },
    contextmenu: {
      type: Function,
      default: e => e,
    },
  },
  watch: {
    system: {
      immediate: true,
      handler: 'changSystemHandler',
    },
    items: {
      deep: true,
      handler: 'changItemsHandler',
    },
    // BEGIN:: Commented out.by.KWShin.2023.06.28.
    values: {
      handler: 'changeValuesHandler',
    },
    // FINISH:: Commented out.by.KWShin.2023.06.28.
    isDark: {
      handler: 'changColorHandler',
    },
  },
  data: () => ({
    options: {},
    systemChanged: true,
    xAxisData: [],
    chartReady: (echart, caller) => {
      caller.$parent.dataZoomSelect();
    },
    loading: false,
  }),
  computed: {
    levels() {
      return [
        {
          index: 0,
          response: 100,
          label: '<= 100 ms',
          color: '#87CC66',
        },
        {
          index: 1,
          response: 1000,
          label: '<= 1 s',
          color: '#7BBAE7',
        },
        {
          index: 2,
          response: 5000,
          label: '<= 5 s',
          color: '#FFDD00',
        },
        {
          index: 3,
          response: 10000,
          label: '<= 10 s',
          color: '#FF7800',
        },
        {
          index: 4,
          response: -1,
          label: '> 10 s',
          color: '#FF0000',
        },
      ];
    },
    echart() {
      return this.$refs.chart.$refs.chart.chart;
    },
    chartInit() {
      return {
        grid: this.grid,
        xAxis: {
          type: 'category',
          data: [],
          axisLabel: {
            fontWeight: 'normal',
            fontSize: 9,
            color: '#bbbbaa',
          },
        },
        yAxis: {
          type: 'value',
          min: 0,
          axisLabel: {
            formatter: value => (value > 0 && value <= this.yAxisMax ? `${value / 1000}s` : ''),
            fontWeight: 'normal',
            fontSize: 9,
            color: '#bbbbaa',
          },
        },
        toolbox: {
          itemSize: 0,
          feature: {
            dataZoom: {},
            brush: { type: ['rect', 'polygon', 'clear'] },
          },
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross',
            label: {
              formatter: param => this.axisPointerLabelFormatter(param),
            },
          },
          formatter: params => this.tooltipFormatter(params),
          position: (pos, params, el, elRect, size) => {
            var obj = { top: 10 };
            obj[['left', 'right'][+(pos[0] < size.viewSize[0] / 2)]] = 30;
            return obj;
          },
        },
      };
    },
  },
  methods: {
    changSystemHandler() {
      this.systemChanged = true;
      this.loading = true;
    },
    changItemsHandler() {
      if (!this.systemChanged) this.changColorHandler();
    },
    changeValuesHandler() {
      this.$nextTick(() => {
        if (this.systemChanged) {
          this.options = this.getOptions();
          this.loading = false;
        } else {
          this.updatedSeries();
        }
        this.systemChanged = false;
      });
    },
    changColorHandler() {
      this.options.series.forEach((value, index) => (this.options.series[index].itemStyle.opacity = this.items[index].active ? 1 : 0.3));
    },
    getOptions() {
      // console.log('in Scatter.vue, in getOptions(), called');
      let xAxisData = [...new Set(this.values.reduce((acc, value) => acc.concat(value), []).map(item => item.timestamp))].sort((a, b) => a - b);
      const spliceSize = xAxisData.length > this.xAxisMax ? xAxisData.length - this.xAxisMax : 0;
      if (spliceSize) xAxisData.splice(0, spliceSize); // x좌표 기준 데이터 표시 제한
      this.xAxisData = xAxisData;
      return {
        xAxis: {
          type: 'time',
          axisLabel: {
            formatter: value => {
              return format(new Date(parseInt(value)), 'HH:mm:ss');
            },
          },
        },
        yAxis: {
          min: 0 /* 음수 값은 제외 */,
          max: this.yAxisMax + 500,
        },
        series: this.getSeries(),
      };
    },
    // 초기 buffer data 를 이용한 series 지정
    getSeries() {
      // console.log('in getSeries(), this.values=', this.values);
      const series = this.values.map((value, index) => {
        const item = this.items[index];
        const data = [];
        value.forEach(val => {
          if (this.xAxisData.findIndex(xAxis => xAxis == val.timestamp) > -1) {
            data.push([new Date(val.timestamp), val.yAxisValue, `${val.responseTime}=${val.responseCount}`]);
          }
        });
        // value
        //   .filter(val => this.xAxisData.findIndex(xAxis => xAxis == val.timestamp) > -1)
        //   .map()

        return {
          type: 'scatter',
          name: item.instanceId + item.id,
          symbol: 'circle',
          symbolSize: 2,
          hoverAnimation: false,
          itemStyle: {
            color: param => (item.active ? this.getLevel(param.value[1]).color : this.getThemeColor(this.chartColor.splitArea, 10, 20)),
            opacity: item.active ? 1 : 0.3,
          },
          z: item.active ? 10 : 0,
          data,
        };
      });
      // console.log('in getSeries(), series=', series);
      return series;
    },
    updatedSeries() {
      // console.log('in Scatter.vue, in updatedSeries(), called');
      const addXAxis = [...new Set(this.values.reduce((acc, value) => acc.concat(value), []).map(item => item.timestamp))].sort((a, b) => a - b);
      const spliceSize = addXAxis.length > this.xAxisMax ? addXAxis.length - this.xAxisMax : 0;
      if (spliceSize) addXAxis.splice(0, spliceSize); // x좌표 기준 데이터 표시 제한

      const diffAddXAxis = [...new Set(addXAxis.filter(val => !this.xAxisData.includes(val)))]; // 기존 대비 추가된 부분
      if (diffAddXAxis.length) {
        this.xAxisData.splice(0, diffAddXAxis.length); // 기존 대비 추가된 부분만큼만 최 좌측 제거
        this.xAxisData = this.xAxisData.concat(diffAddXAxis); // 기존대비 추가된 부분 추가
      }

      this.values.forEach((value, index) => {
        // 최 좌측 데이터 삭제
        let data = this.options.series[index].data;
        let size = data.length;
        for (let idx = size - 1; idx >= 0; idx--) {
          if (this.xAxisData[0] > data[idx][0]) data.splice(idx, 1);
        }
        // 신규 데이터 추가
        this.values[index]
          .filter(val => this.xAxisData.findIndex(xAxis => xAxis == val.timestamp) > -1)
          .forEach(val => {
            this.options.series[index].data.push([new Date(val.timestamp), val.yAxisValue, `${val.responseTime}=${val.responseCount}`]);
          });
      });
    },
    getLevel(value) {
      let levelIndex = this.levels.length - 1;
      this.levels.some((level, index) => {
        if (value <= level.response) {
          levelIndex = index;
          return true;
        }
      });
      return this.levels[levelIndex];
    },
    axisPointerLabelFormatter(param) {
      return param.axisDimension == 'y' ? comma(param.value.toFixed(0)) : format(new Date(parseInt(param.value)), 'yyyy-MM-dd HH:mm:ss');
    },
    tooltipFormatter(params) {
      let list = [];
      let label = params[0].axisValueLabel;
      const items = {};
      // 활성화 된 item 만 표시
      params = params.filter(param => this.items[param.seriesIndex].active);
      // 응답속도 구간별 count
      params.forEach(param => {
        if (!items[param.seriesName]) items[param.seriesName] = {};
        const response = param.data[2].split('=');
        const responseTime = parseInt(response[0]); // y 좌표의 실제 값
        const responseCount = parseInt(response[1]); // y 좌표 실제 값에 해당하는 count
        const level = this.getLevel(responseTime);
        if (!items[param.seriesName][level.index]) items[param.seriesName][level.index] = 0;
        items[param.seriesName][level.index] += responseCount;
      });
      Object.keys(items).forEach(seriesName => {
        const it = this.items.find(item => item.id === seriesName);
        // const contents = [this.circle(it.color), `<span style="color:${it.color};font-weight:900;">`, `${it.id}`, '</span>'];
        const contents = [this.circle(it.color), `<span style="color:${it.color};font-weight:900;">`, `${it.shortAlias}`, '</span>'];
        list.push(contents.join(''));
        Object.keys(items[seriesName]).forEach(levelIndex => {
          const level = this.levels[levelIndex];
          const contents = [`<span style="color:${level.color};font-weight:900;margin:0 0 0 10px;">`, `${level.label}`, '</span>', ' : ', items[seriesName][levelIndex]];
          list.push(contents.join(''));
        });
      });
      list.unshift(label);
      return list.join('<br/>');
    },
    responseTimeBound() {},
    dataZoomSelect() {
      this.echart.dispatchAction({
        type: 'takeGlobalCursor',
        key: 'dataZoomSelect',
        dataZoomSelectActive: true,
      });
      this.echart.on('datazoom', p => this.selected(p));
    },
    dataExists(xBounds, yBounds) {
      return this.options.series
        .reduce((acc, series) => acc.concat(series.data), [])
        .some(item => {
          const isBoundX = xBounds[0] <= item[0] && item[0] <= xBounds[1];
          const isBoundY = yBounds[0] <= item[1] && item[1] <= yBounds[1];
          return isBoundX && isBoundY;
        });
    },
    selected(p) {
      if (!p.batch[0].startValue) return;
      const xAxisBounds = [p.batch[0].startValue, p.batch[0].endValue].sort((a, b) => a - b);
      const values = [Math.floor(p.batch[1].startValue), Math.ceil(p.batch[1].endValue)].sort((a, b) => a - b);
      if (!this.dataExists(xAxisBounds, values)) {
        this.echart.dispatchAction({ type: 'restore' });
        return;
      }
      // 지정된 y 좌표 최대값보다 큰 범위가 선택되었을 경우
      // 선택된 해당 범위내에 가장 큰 값을 반환한다.
      // 단, 계산된 가장 큰 값은 chart 표시 값이 아니며,
      // 실제 값을 기준으로 한다.
      if (values[1] > this.yAxisMax) {
        // 현재 활성화된 instance 내에서 발췌한다.
        /*
        values[1] = this.items
          .filter(item => item.active)
          .map(item => {
            const sereisData = this.options.series.find(s => s.name == item.id).data;
            console.log(sereisData);
            const max = sereisData.filter(d => d[0] >= xAxisBounds[0] && d[0] <= xAxisBounds[1]).reduce((max, item) => (item[2] > max ? item[2] : max), 0);
            return max;
          })
          .reduce((max, val) => (val > max ? val : max), 0);
        */
        // 모든 instance 내에서 발췌한다.
        values[1] = this.options.series
          .map(series => {
            return series.data
              .filter(position => {
                // x 좌표 범위내의 값
                return position[0] >= xAxisBounds[0] && position[0] <= xAxisBounds[1];
              })
              .reduce((max, position) => {
                // y 좌표 값 내의 max 값
                const yAxisValue = parseInt(position[2].split('=')[0]);
                return yAxisValue > max ? yAxisValue : max;
              }, 0);
          })
          .reduce((max, val) => {
            // series 별 max 값 중 최대값
            return val > max ? val : max;
          }, 0);
      }
      this.echart.dispatchAction({ type: 'restore' });
      this.selectedHandler(
        {
          dates: xAxisBounds,
          values,
          system: this.system,
          descendants: this.items.filter(item => item.active).map(item => ({ systemId: item.systemId, id: item.id })),
        },
        this,
      );
    },
  },
};
</script>
